import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiHttp } from '@core/api/api-http';
import { OpportunityApiHttp } from '@core/api/opportunity-api-http';
import { assert, deepCopy } from '@core/prelude';
import { isReverseMortgage } from '@shared/utilities/product/product-feature.util';
import {
  RecommendationType,
  ProductFeature,
  RateType,
  RateTypes,
  isPackage,
  Repayments,
  ProductStructure,
  ProductRate,
  RepaymentType,
  EditProduct,
  EditPackage,
  ProductSelection,
  ProductSearchResult,
  PackageProduct,
  RepaymentSchedule,
  ProductSelectionResponse,
  RiskGrade,
  ProductSelectionInputs,
  LvrAndMaxCap,
  ServicingOverride
} from '@features/opportunities/models/product-selection/product-selection.model';
import { ProductSelectionBorrowingCapacityResult } from '@shared/models/borrowing-capacity';
import { PaymentFrequency } from '@shared/models/income';

import { flattenCollection } from './contract';
import { collectionFromArray, collectionIter } from '../models/collection';

export type SettlementProductMeta = {
  maxLoanTerm: number | null;
  maxInterestOnlyTerm: number | null;
  isReverseMortgage: boolean;
};

@Injectable({
  providedIn: 'root'
})
export class ProductSelectionService {
  constructor(private http: OpportunityApiHttp) {}

  isPackageProduct(payload: any): boolean {
    return (
      payload.packageProduct != null &&
      flattenCollection(payload.packageProduct.products).length > 0
    );
  }

  calculateRepayments(
    structure: ProductStructure,
    loanAmount: number,
    frequency: PaymentFrequency,
    balloonAmount: number | undefined = undefined,
    capitalizeInterestOnlyPayments: boolean | undefined = undefined
  ): Observable<Repayments> {
    let request = {
      structure: structure,
      loanAmount: loanAmount,
      repaymentFrequency: frequency,
      balloonAmount: balloonAmount,
      capitalizeInterestOnlyPayments: capitalizeInterestOnlyPayments,
    };
    return this.http.post(
      `calculate-repayments`,
      request,
      ApiHttp.getStandardOptions()
    );
  }

  getProductSelectionInputs(
    opportunityId: string
  ): Observable<ProductSelectionInputs> {
    return this.http.get(
      `opportunities/${opportunityId}/productSelection/inputs`,
      ApiHttp.getStandardOptions()
    );
  }

  calculateRepaymentSchedule(
    packageProducts: PackageProduct[]
  ): Observable<RepaymentSchedule[]> {
    let feesAsCollections = packageProducts.map((packageProduct) => {
      return collectionFromArray(packageProduct.product.fees);
    });

    let payload = deepCopy(packageProducts) as any[];

    payload.forEach((packageProduct: any, index) => {
      packageProduct.product.fees = feesAsCollections[index];
      collectionIter(
        packageProduct.product.fees,
        (_, fee: any) => (fee.id = undefined)
      );
    });

    return this.http.post(
      `calculate-repayment-schedule`,
      payload,
      ApiHttp.getStandardOptions()
    );
  }

  getProductSelections(
    opportunityId: string
  ): Observable<ProductSelectionResponse> {
    return this.http
      .get(
        `opportunities/${opportunityId}/productSelection`,
        ApiHttp.getStandardOptions()
      )
      .pipe(
        map((payload: any) => {
          let shortlistArray: Array<any> = flattenCollection(
            payload.selections
          );
          let optionList: Array<ProductSelection> = shortlistArray.map(
            (productSelection) => {
              if (
                payload.recommendation?.productSelection == productSelection.id
              ) {
                productSelection.recommendation = payload.recommendation.type;
              }
              if (this.isPackageProduct(productSelection)) {
                productSelection.singleProduct = null;
                productSelection.packageProduct.products = flattenCollection(
                  productSelection.packageProduct.products
                );
                (productSelection.packageProduct.products as any[]).forEach(
                  (packageProduct) => {
                    packageProduct.product.fees = flattenCollection(
                      packageProduct.product.fees
                    );
                  }
                );
              } else {
                productSelection.packageProduct = null;
                productSelection.singleProduct.fees = flattenCollection(
                  productSelection.singleProduct.fees
                );
              }
              return productSelection;
            }
          );
          let sortedSelections = optionList.sort((a, b) => {
            function getRepayments(details: ProductSelection) {
              if (isPackage(details)) {
                return (
                  details.packageProduct?.products
                    .map(
                      (packageProduct) => packageProduct.product.totalRepayments
                    )
                    .reduce(
                      (totalRepayments, repayments) =>
                        totalRepayments + repayments,
                      0
                    ) ?? 0
                );
              } else {
                return details.singleProduct?.totalRepayments ?? 0;
              }
            }
            if (a.recommendation != null) {
              return -1;
            } else if (b.recommendation != null) {
              return 1;
            } else {
              return getRepayments(a) - getRepayments(b);
            }
          });
          return {
            selections: sortedSelections,
            otherProductsUnsuitable: payload.otherProductsUnsuitable
          };
        })
      );
  }

  getRecommendedProduct(
    opportunityId: string
  ): Observable<ProductSelection | null> {
    return this.getProductSelections(opportunityId).pipe(
      map((response) => {
        let recommendedProduct = response.selections.find(
          (productSelection) => productSelection.recommendation != null
        );
        return recommendedProduct ? recommendedProduct : null;
      })
    );
  }

  saveProductSelectionRecommendation(
    opportunityId: string,
    productSelectionId: string,
    recommendationType: RecommendationType
  ): Observable<object> {
    return this.http.put(
      `opportunities/${opportunityId}/productSelection/recommendation`,
      {
        productSelection: productSelectionId,
        type: recommendationType
      },
      ApiHttp.getStandardOptions()
    );
  }

  saveOtherProductsUnsuitable(
    opportunityId: string,
    otherProductsUnsuitable: boolean
  ): Observable<object> {
    return this.http.put(
      `opportunities/${opportunityId}/productSelection`,
      {
        otherProductsUnsuitable: otherProductsUnsuitable
      },
      ApiHttp.getStandardOptions()
    );
  }

  getSingleProduct(
    opportunityId: string,
    productSelectionId: string
  ): Observable<EditProduct> {
    return this.http
      .getWithMetadata(
        `opportunities/${opportunityId}/productSelection/selections/${productSelectionId}`
      )
      .pipe(
        map((payload: any) => {
          assert(
            !this.isPackageProduct(payload),
            'Expected Single Product but received Multi Products.'
          );
          payload.singleProduct.fees = flattenCollection(
            payload.singleProduct.fees
          );
          return payload.singleProduct;
        })
      );
  }

  getPackageProduct(
    opportunityId: string,
    productSelectionId: string
  ): Observable<EditPackage> {
    return this.http
      .getWithMetadata(
        `opportunities/${opportunityId}/productSelection/selections/${productSelectionId}`
      )
      .pipe(
        map((payload: any) => {
          assert(
            this.isPackageProduct(payload),
            'Expected Multi Products but received Single Product.'
          );
          payload.singleProduct = null;
          payload.packageProduct.products = flattenCollection(
            payload.packageProduct.products
          );
          (payload.packageProduct.products as any[]).forEach(
            (packageProduct) => {
              packageProduct.product.fees = flattenCollection(
                packageProduct.product.fees
              );
            }
          );
          return payload.packageProduct;
        })
      );
  }

  getSettlementProductMetadata(
    opportunityId: string,
    productSelectionId: string
  ): Observable<Map<number, SettlementProductMeta>> {
    return this.http
      .getWithMetadata(
        `opportunities/${opportunityId}/productSelection/selections/${productSelectionId}`
      )
      .pipe(
        map((payload: any) => {
          const meta = new Map<number, SettlementProductMeta>();
          const extract = (product: any) => {
            const id = product?.productId?.value;
            if (id == null) return;
            meta.set(id, {
              maxLoanTerm: product?.productTerm?.max?.value ?? null,
              maxInterestOnlyTerm: product?.productTerm?.maxInterestOnlyTerm?.value ?? null,
              isReverseMortgage: isReverseMortgage(product?.features)
            });
          };
          if (this.isPackageProduct(payload)) {
            flattenCollection(payload.packageProduct.products).forEach(
              (p: any) => extract(p.product)
            );
          } else if (payload.singleProduct) {
            extract(payload.singleProduct);
          }
          return meta;
        })
      );
  }

  getApplicableRate(
    opportunityId: string,
    productId: number,
    loanAmount: number,
    proPack: boolean,
    repaymentType: RepaymentType
  ): Observable<ProductRate[]> {
    let params = {
      headers: ApiHttp.getStandardOptions().headers,
      params: {
        loanAmount: loanAmount,
        proPack: proPack,
        repaymentType: repaymentType
      }
    };

    return this.http.get(
      `opportunities/${opportunityId}/productSelection/applicable-rate/${productId}`,
      params
    );
  }

  calculateServicing(
    opportunityId: string,
    payload: any
  ): Observable<ProductSelectionBorrowingCapacityResult> {
    return this.http.post(
      `opportunities/${opportunityId}/productSelection/calculate-servicing`,
      payload,
      ApiHttp.getStandardOptions()
    );
  }

  saveServicingOverride(
    opportunityId: string,
    productSelectionId: string,
    reason: string
  ): Observable<{ referenceNumber: string }> {
    return this.http.post(
      `opportunities/${opportunityId}/productSelection/${productSelectionId}/servicing-override`,
      { reason },
      ApiHttp.getStandardOptions()
    );
  }

  queryAvailableProducts(
    opportunityId: string,
    rateType: RateTypes,
    repaymentType: RepaymentType,
    features: Array<string>,
    riskGrade: RiskGrade,
    rateTypes: RateType[]
  ): Observable<Array<ProductSearchResult>> {
    let payload = {
      rateType: rateType,
      repaymentType: repaymentType,
      features: features,
      rateTypes: rateTypes,
    };

    return this.http
      .post<Array<ProductSearchResult>>(
        `opportunities/${opportunityId}/productSelection/options`,
        payload,
        ApiHttp.getStandardOptions()
      )
      .pipe(
        map((results) =>
          results.sort((a, b) => {
            if (
              riskGrade == RiskGrade.AAA ||
              a.product.riskGrade == b.product.riskGrade
            ) {
              return a.product.structure[0].rate - b.product.structure[0].rate;
            } else if (a.product.riskGrade == RiskGrade.AAA) {
              return 1;
            } else {
              return -1;
            }
          })
        )
      );
  }

  addProductSelections(
    opportunityId: string,
    payload: any
  ): Observable<object> {
    // TODO: Decide / Change on Opp API whether to update Package to Multi Products or add a new field for Multi Products that would map to Package
    payload.selectionType = payload.selectionType === "Multi Products" ? "Package" : 
                             payload.selectionType === "Single Product" ? "Single" : 
                             payload.selectionType;
    
    return this.http.post(
      `opportunities/${opportunityId}/productSelection`,
      payload,
      ApiHttp.getStandardOptions()
    );
  }

  updateProductSelection(
    opportunityId: string,
    productSelectionId: string,
    change: any
  ): Observable<object> {
    return this.http.put(
      `opportunities/${opportunityId}/productSelection/selections/${productSelectionId}`,
      change,
      ApiHttp.getStandardOptions()
    );
  }

  deleteProductSelection(
    opportunityId: string,
    productSelectionId: string
  ): Observable<object> {
    return this.http.delete(
      `opportunities/${opportunityId}/productSelection/selections/${productSelectionId}`
    );
  }

  deleteAllProductSelections(opportunityId: string): Observable<object> {
    return this.http.put(
      `opportunities/${opportunityId}/productSelection`,
      {
        selections: null,
        recommendation: null
      },
      ApiHttp.getStandardOptions()
    );
  }

  getLoanTypeFeatures(
    opportunityId: string
  ): Observable<Array<ProductFeature>> {
    return this.http.get(
      `opportunities/${opportunityId}/productSelection/features`,
      ApiHttp.getStandardOptions()
    );
  }

  getLvrAndLmiMaxCap(
    opportunityId: string,
    productMaxLvr: number,
    uncapitalisedLoanAmount: number,
    providerName: string,
    productRateId: string,
    overrideLmiAmount?: number
  ): Observable<LvrAndMaxCap> {
    return this.http.post(
      `opportunities/${opportunityId}/funds-position/recalculate-lvr-and-lmi`,
      {
        productMaxLvr: productMaxLvr,
        loanAmount: uncapitalisedLoanAmount,
        providerName: providerName,
        productRateId: productRateId,
        overrideLmiAmount: overrideLmiAmount
      },
      ApiHttp.getStandardOptions()
    );
  }

  getMaxBalloonAmount(
    opportunityId: string,
    loanTerm: number,
    productId: number
  ): Observable<number> {
    return this.http.post(
      `opportunities/${opportunityId}/productSelection/calculate-max-balloon-amount`,
      {
        loanTerm: loanTerm,
        productId: productId
      },
      ApiHttp.getStandardOptions()
    );
  }
}
